import { routeUrl } from "@/app/routes";
import type { NotificationRow } from "@/features/notifications/schemas/notification.schema";

/// Build a workspace-relative deep link from a Notification row's
/// `resourceType` + `resourceId`. Mirrors the registry's `deepLink`
/// templates on the shore side. Returns the workspace home for rows
/// without a known target, the bell + inbox still render them, just
/// without a click destination.
export function notificationDeepLink(workspaceSlug: string, notification: NotificationRow): string {
  const home = routeUrl.workspace.home(workspaceSlug);
  const { resourceType, resourceId } = notification;
  if (!resourceType || !resourceId) return home;

  switch (resourceType) {
    case "feedback":
      return routeUrl.feedback.detail(workspaceSlug, resourceId);
    case "kudos":
      return routeUrl.kudos.detail(workspaceSlug, resourceId);
    case "one-on-one":
      return routeUrl.oneOnOnes.detail(workspaceSlug, resourceId);
    case "todo":
      return routeUrl.todos.detail(workspaceSlug, resourceId);
    case "pulse-survey-delivery":
      return routeUrl.pulseSurveys.respond(workspaceSlug, resourceId);
    case "pulse-answer-thread":
      return routeUrl.pulse.conversation(workspaceSlug, resourceId);
    case "pulse-answer-thread-admin":
      return routeUrl.pulseSurveys.answerThread(workspaceSlug, resourceId);
    case "pulse-action":
      return routeUrl.actions.detail(workspaceSlug, resourceId);
    // The HRMS approvals inbox has no per-request detail route, so every
    // approval-request notification deep-links to the inbox list, not a
    // specific request. resourceId is present on the row (the guard above
    // requires it) but deliberately unused here.
    case "approval-request":
      return routeUrl.approvals.list(workspaceSlug);
    // There is no per-application detail route in the leave module.
    // Recipients of a filed application are the approvers, so the row
    // deep-links to the "To decide" tab, the module's own decision queue,
    // rather than a specific application. resourceId (the application id)
    // is present on the row but deliberately unused here.
    case "leave-decision":
      return routeUrl.leave.decide(workspaceSlug);
    case "leave-application":
      return routeUrl.leave.mine(workspaceSlug);
    // There is no per-period detail route either. Recipients are workspace
    // admins, and the action they need is creating the next leave period,
    // which lives on the leave settings page. resourceId (the leave period
    // id) is present on the row but deliberately unused here.
    case "leave-period":
      return routeUrl.leave.setup(workspaceSlug);
    case "comment": {
      // Comments deep-link to the parent thread, not the comment itself.
      // The payload's `parentType`/`parentId` lives in the title; we fall
      // back to the workspace root if a comment row reaches the UI
      // without those (registry should populate them).
      return home;
    }
    // ESS-01. A payslip notification opens the payslip; a pay-details
    // decision opens the page that explains it. Both existed as shore-side
    // deep links before this switch knew about them, which is why the IN-APP
    // bell fell back to workspace home for both while the EMAIL link worked.
    case "salary-slip":
      return routeUrl.payroll.myPayslips(workspaceSlug, resourceId);
    case "payroll-details":
      return routeUrl.payroll.myDetails(workspaceSlug);
    case "workspace":
      return routeUrl.workspace.settings(workspaceSlug);
    default:
      return home;
  }
}
